<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Stack, SelectCard, Text } from '@Shelly/shelly-design-system';
	import { flowEditorState } from '../../../stores/flow_editor';

	export let nodeId: string;

	$: node = $flowEditorState.nodes.find((n) => n.id === nodeId);
	$: nodeData = node?.data || ({} as any);
	$: gateType = (nodeData.gateType as string) || 'or';
	$: conditionCount = Number(nodeData.conditionCount || 2);

	const gateTypes = [
		{
			value: 'or',
			title: 'OR',
			description: 'Passes TRUE when ANY connected condition is true.',
		},
		{
			value: 'and',
			title: 'AND',
			description: 'Passes TRUE when ALL connected conditions are true.',
		},
		{
			value: 'nor',
			title: 'NOR',
			description: 'Passes TRUE when NONE of the connected conditions are true (inverted OR).',
		},
	];

	function setGateType(type: string) {
		if (!node) return;
		flowEditorState.updateNode(nodeId, {
			data: { ...nodeData, gateType: type } as any,
		});
	}

	function setConditionCount(count: number) {
		if (!node) return;
		const clamped = Math.max(2, Math.min(10, Math.round(count)));
		flowEditorState.updateNode(nodeId, {
			data: { ...nodeData, conditionCount: clamped } as any,
		});
	}
</script>

<div class="logic-gate-inspector">
	<Stack direction="row" gap="m">
		<Text variant="h3">
			{$format('flow_logic_gate_settings', { default: 'Logical Gate Settings' })}
		</Text>

		<Text variant="body" size="s" class="section-label">
			{$format('flow_gate_type', { default: 'Gate Type' })}
		</Text>

		<div class="gate-options">
			{#each gateTypes as gate}
				<SelectCard
					variant="radio"
					type="horizontal"
					class="width-full"
					selected={gateType === gate.value}
					on:click={() => setGateType(gate.value)}
					title={gate.title}
					descriptiveText={gate.description}
					size="m"
				/>
			{/each}
		</div>

		<div class="input-count-section">
			<Text variant="body" size="s">
				{$format('flow_input_count', { default: 'Number of inputs' })}
			</Text>

			<div class="count-stepper">
				<button
					class="stepper-btn"
					disabled={conditionCount <= 2}
					on:click={() => setConditionCount(conditionCount - 1)}
					aria-label="Decrease inputs"
				>
					<i class="fas fa-minus" />
				</button>
				<div class="count-display">{conditionCount}</div>
				<button
					class="stepper-btn"
					disabled={conditionCount >= 10}
					on:click={() => setConditionCount(conditionCount + 1)}
					aria-label="Increase inputs"
				>
					<i class="fas fa-plus" />
				</button>
			</div>
		</div>
	</Stack>
</div>

<style>
	.logic-gate-inspector {
		padding: 16px;
	}

	:global(.section-label) {
		color: #b0b0b0 !important;
	}

	.gate-options {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.input-count-section {
		display: flex;
		flex-direction: column;
		gap: 8px;
		margin-top: 8px;
		padding-top: 12px;
		border-top: 1px solid rgba(255, 255, 255, 0.08);
	}

	.count-stepper {
		display: flex;
		align-items: center;
		gap: 12px;
	}

	.stepper-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 36px;
		height: 36px;
		border-radius: 8px;
		border: 1px solid rgba(255, 255, 255, 0.15);
		background: rgba(255, 255, 255, 0.05);
		color: #fff;
		cursor: pointer;
		font-size: 0.8rem;
		transition: all 0.15s ease;
	}

	.stepper-btn:hover:not(:disabled) {
		background: rgba(59, 130, 246, 0.2);
		border-color: rgba(59, 130, 246, 0.4);
	}

	.stepper-btn:disabled {
		opacity: 0.35;
		cursor: not-allowed;
	}

	.count-display {
		font-size: 1.25rem;
		font-weight: 600;
		color: #fff;
		min-width: 2rem;
		text-align: center;
	}
</style>
